import React, { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "@mui/material";
import { Swiper, SwiperSlide } from "swiper/react";

import { useProgressiveImage } from "hooks";
import { ProgressiveImage, Icon } from "components";
import { selectFocusedPhoto, selectFocusedPhotos, setFocusedPhotoIdx, getFocuedIndex, getSelectedBadgePhotoId } from "slices";
import { getIsMaximized } from "slices/window-slice/index";

const FocusedPhotoShow = () => {
  const isMaximized = useSelector(getIsMaximized);
  const focusedIdx = useSelector(getFocuedIndex);
  const photos = useSelector(selectFocusedPhotos);
  const focusedPhoto = useSelector(selectFocusedPhoto);
  const selectedBadgeId = useSelector(getSelectedBadgePhotoId);
  const src = focusedPhoto?.image_url || focusedPhoto?.thumbnail_url;

  const dispatch = useDispatch();
  const swiperRef = useRef(null);

  const { isLoading } = useProgressiveImage(src);

  useEffect(() => {
    if (swiperRef?.current) {
      swiperRef.current.slideTo(focusedIdx);
    }
  }, [focusedIdx]);

  const getSwiperRef = (swiperInstance) => {
    swiperInstance.slideTo(focusedIdx);
    swiperRef.current = swiperInstance;
  };

  const go2Prev = () => {
    if (focusedIdx > 0) {
      swiperRef.current.slidePrev();
      dispatch(setFocusedPhotoIdx(focusedIdx - 1));
    } else {
      const currIndex = Object.keys(photos || {}).length - 1;
      swiperRef.current.slideTo(currIndex);
      dispatch(setFocusedPhotoIdx(currIndex));
    }
  };

  const go2Next = () => {
    if (focusedIdx < Object.keys(photos || {}).length - 1) {
      swiperRef.current.slideNext();
      dispatch(setFocusedPhotoIdx(focusedIdx + 1));
    } else {
      swiperRef.current.slideTo(0);
      dispatch(setFocusedPhotoIdx(0));
    }
  };

  const content = useMemo(() => {
    if (!focusedPhoto) {
      return null;
    }

    return (
      <div className="hayley-relative hayley-h-full hayley-w-full">
        <Swiper onSwiper={getSwiperRef}>
          {Object.keys(photos || {}).map((key, idx) => {
            let d = photos[key];

            return (
              <SwiperSlide key={idx}>
                <ProgressiveImage
                  classes={{
                    root: "hayley-h-[calc(100dvh-335px)] hayley-w-full hayley-select-none hayley-overflow-hidden hayley-bg-custom-9 hayley-text-center",
                    img: "hayley-h-full hayley-w-full hayley-object-contain hayley-object-center",
                  }}
                  src={d?.image_url}
                  alt={d?.name}
                  selectedId={selectedBadgeId}
                  testId={`photosView_desktop_${selectedBadgeId}_image`}
                />
              </SwiperSlide>
            );
          })}
        </Swiper>

        {Object.keys(photos || {}).length > 1 && (
          <Button
            className={`hayley-absolute ${
              isMaximized ? "hayley-left-6 md:hayley-left-8" : "hayley-left-6"
            }  hayley-top-1/2 hayley-z-10 hayley-flex hayley-h-10 hayley-w-10 hayley-min-w-[40px] -hayley-translate-y-1/2 hayley-cursor-pointer hayley-items-center hayley-justify-center hayley-rounded-full hayley-bg-secondary/30 hayley-shadow-lg hover:hayley-bg-secondary/80`}
            onClick={go2Prev}
            data-testid="photosView_leftArrow"
          >
            <Icon iconType="Chevron" className="hayley-h-6 hayley-w-6 hayley-rotate-180 hayley-text-white" />
          </Button>
        )}

        {Object.keys(photos || {}).length > 1 && (
          <Button
            className={`hayley-absolute ${
              isMaximized ? "hayley-right-6 md:hayley-right-8" : "hayley-right-6"
            }   hayley-top-1/2 hayley-z-10 hayley-flex hayley-h-10 hayley-w-10 hayley-min-w-[40px] -hayley-translate-y-1/2 hayley-cursor-pointer hayley-items-center hayley-justify-center hayley-rounded-full hayley-bg-secondary/30 hayley-shadow-lg hover:hayley-bg-secondary/80`}
            onClick={go2Next}
            data-testid="photosView_rightArrow"
          >
            <Icon iconType="Chevron" className="hayley-h-6 hayley-w-6 hayley-text-white" />
          </Button>
        )}
      </div>
    );
  }, [focusedPhoto, focusedIdx, isLoading]);

  return <div className={`hayley-hidden hayley-h-full hayley-w-full hayley-bg-gray-100 sm:hayley-flex`}>{content}</div>;
};

export default FocusedPhotoShow;
